import React, { useState, useEffect, useCallback } from 'react';
import {
  Card, Button, Table, Tag, Space, Typography, Modal, Form, Input,
  Select, Switch, Tooltip, message, Alert, Divider, Row, Col,
  Statistic, Badge, Popconfirm, InputNumber, Tabs,
} from 'antd';
import {
  PlusOutlined, CopyOutlined, DeleteOutlined, KeyOutlined,
  CheckCircleOutlined, CloseCircleOutlined, ApiOutlined,
  CodeOutlined, GlobalOutlined, ExclamationCircleOutlined,
  BookOutlined,
} from '@ant-design/icons';

const { Text, Title, Paragraph } = Typography;
const { TabPane } = Tabs;

const API = '/api/v1';
const h = () => ({ Authorization: `Bearer ${localStorage.getItem('spo_auth_token')}`, 'Content-Type': 'application/json' });

const api = {
  listKeys: () => fetch(`${API}/api-keys`, { headers: h() }).then(r => r.json()),
  createKey: (body) => fetch(`${API}/api-keys`, { method: 'POST', headers: h(), body: JSON.stringify(body) }).then(r => r.json()),
  toggleKey: (id, body) => fetch(`${API}/api-keys/${id}`, { method: 'PATCH', headers: h(), body: JSON.stringify(body) }).then(r => r.json()),
  deleteKey: (id) => fetch(`${API}/api-keys/${id}`, { method: 'DELETE', headers: h() }).then(r => r.json()),
};

const PERM_OPTIONS = [
  { value: 'fb_ads:read', label: '讀取廣告', color: 'blue' },
  { value: 'fb_ads:write', label: '建立/更新廣告', color: 'green' },
  { value: 'fb_ads:publish', label: '發布廣告', color: 'orange' },
  { value: 'google_ads:read', label: 'Google Ads 讀取成效', color: 'cyan' },
  { value: 'google_ads:write', label: 'Google Ads 建立廣告', color: 'purple' },
];

function CopyBtn({ text, label }) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <Button size="small" icon={copied ? <CheckCircleOutlined /> : <CopyOutlined />}
      style={{ color: copied ? '#52c41a' : undefined }} onClick={copy}>
      {label || (copied ? '已複製' : '複製')}
    </Button>
  );
}

// ── API Doc Display ────────────────────────────────────────────────────────────
function ApiDocPanel() {
  const BASE = window.location.origin;
  const sections = [
    {
      title: '認證方式',
      content: `所有 API 請求需在 Header 加入：\nX-API-Key: spo_your_key_here`,
      lang: 'http',
    },
    {
      title: '1. 建立廣告草稿',
      method: 'POST', path: '/api/v1/public/fb-ads',
      desc: '建立一則廣告草稿，設定標題、內文、CTA 連結',
      example: `curl -X POST ${BASE}/api/v1/public/fb-ads \\
  -H "X-API-Key: spo_your_key" \\
  -H "Content-Type: application/json" \\
  -d '{
    "title": "活動標題",
    "body": "廣告內文描述...",
    "cta_url": "https://your-site.com/register",
    "cta_label": "立即報名",
    "page_name": "Your Brand"
  }'`,
      response: `{ "ok": true, "ad_id": 1, "status": "draft" }`,
    },
    {
      title: '2. 上傳廣告圖片',
      method: 'POST', path: '/api/v1/public/fb-ads/{ad_id}/image',
      desc: '上傳廣告主圖（建議 1200×628px，JPG/PNG，≤30MB）',
      example: `curl -X POST ${BASE}/api/v1/public/fb-ads/1/image \\
  -H "X-API-Key: spo_your_key" \\
  -F "file=@/path/to/banner.jpg"`,
      response: `{ "ok": true, "image_url": "/api/v1/fb-ads/1/image" }`,
    },
    {
      title: '3. 更新廣告內容',
      method: 'PATCH', path: '/api/v1/public/fb-ads/{ad_id}',
      desc: '更新廣告標題、內文或連結',
      example: `curl -X PATCH ${BASE}/api/v1/public/fb-ads/1 \\
  -H "X-API-Key: spo_your_key" \\
  -H "Content-Type: application/json" \\
  -d '{ "title": "新標題", "cta_url": "https://new-url.com" }'`,
      response: `{ "ok": true, "ad_id": 1, "status": "draft" }`,
    },
    {
      title: '4. 查詢廣告狀態',
      method: 'GET', path: '/api/v1/public/fb-ads/{ad_id}',
      desc: '查詢廣告內容與發布狀態',
      example: `curl ${BASE}/api/v1/public/fb-ads/1 \\
  -H "X-API-Key: spo_your_key"`,
      response: `{
  "ad_id": 1, "title": "...", "status": "draft|published",
  "fb_post_id": "123456789", "created_at": "2026-06-10T..."
}`,
    },
    {
      title: '5. 發布到 Facebook',
      method: 'POST', path: '/api/v1/public/fb-ads/{ad_id}/publish',
      desc: '將廣告草稿發布到 Facebook 粉絲專頁',
      example: `curl -X POST ${BASE}/api/v1/public/fb-ads/1/publish \\
  -H "X-API-Key: spo_your_key"`,
      response: `{ "ok": true, "post_id": "fb_post_id_here" }`,
    },
    {
      title: '6. 建立 Google Ads 搜尋廣告',
      method: 'POST', path: '/api/v1/public/google-ads/campaigns',
      desc: '為其他平台建立 Google Ads 搜尋廣告（需 google_ads:write 權限）',
      example: `curl -X POST ${BASE}/api/v1/public/google-ads/campaigns \\
  -H "X-API-Key: spo_your_key" \\
  -H "Content-Type: application/json" \\
  -d '{
    "campaign_name": "Comboware AI 方案推廣",
    "headlines": ["AI 驅動的雲端平台", "立即免費諮詢"],
    "descriptions": ["Comboware AI 一站式方案，立即了解。", "專業團隊為您服務。"],
    "final_url": "https://www.comboware.ai",
    "keywords": ["ai platform", "combware ai"],
    "daily_budget_micros": 200000000,
    "source": "comboware-ai",
    "source_ref": "product-123"
  }'`,
      response: `{
  "ok": true, "status": "PAUSED",
  "campaign_resource": "customers/9071972107/campaigns/123456789",
  "ad_post_id": 12, "utm_campaign": "gads-comboware-ai-product-123"
}`,
    },
    {
      title: '7. 查詢 Google Ads 成效',
      method: 'GET', path: '/api/v1/public/google-ads/performance',
      desc: '取得 SEM 成效總覽（需 google_ads:read 權限）',
      example: `curl "${BASE}/api/v1/public/google-ads/performance?date_range=LAST_30_DAYS" \\
  -H "X-API-Key: spo_your_key"`,
      response: `{
  "ok": true, "date_range": "LAST_30_DAYS",
  "totals": { "impressions": 12345, "clicks": 321, "cost": 450.5, ... },
  "daily": [ { "date": "2026-07-01", "impressions": 100, "clicks": 5, ... } ],
  "campaigns": [ { "id": 123, "name": "EXT-comboware-ai-...", ... } ]
}`,
    },
    {
      title: '8. 列出 Google Ads campaigns',
      method: 'GET', path: '/api/v1/public/google-ads/campaigns',
      desc: '列出所有 campaign 與生命週期成效（需 google_ads:read 權限）',
      example: `curl ${BASE}/api/v1/public/google-ads/campaigns \\
  -H "X-API-Key: spo_your_key"`,
      response: `{ "ok": true, "campaigns": [ { "id": 123, "name": "...", "status": "PAUSED" } ] }`,
    },
  ];

  const methodColor = { POST: '#52c41a', GET: '#1890ff', PATCH: '#fa8c16', DELETE: '#f5222d' };

  return (
    <div>
      <Alert
        message="📋 完整 API 工作流程"
        description="FB 廣告：建立草稿 → 上傳圖片 → 確認內容 → 發布到 Facebook。Google Ads：以 X-API-Key 建立搜尋廣告並查詢成效。適合 EDM 系統、活動報名平台、comboware.ai 等外部平台整合。"
        type="info" style={{ marginBottom: 20 }}
      />
      {sections.map((s, i) => (
        <Card key={i} size="small" style={{ marginBottom: 12, borderRadius: 8 }}
          title={
            <Space>
              {s.method && <Tag color={methodColor[s.method]} style={{ fontFamily: 'monospace' }}>{s.method}</Tag>}
              <Text code={!!s.path}>{s.path || s.title}</Text>
              {!s.path && <Text strong>{s.title}</Text>}
            </Space>
          }
          extra={s.method && <Text type="secondary" style={{ fontSize: 12 }}>{s.desc}</Text>}
        >
          {s.content && <pre style={{ background: '#f6f8fa', padding: 12, borderRadius: 6, fontSize: 13, margin: 0 }}>{s.content}</pre>}
          {s.example && (
            <>
              <Text type="secondary" style={{ fontSize: 12 }}>Request：</Text>
              <pre style={{ background: '#1e1e1e', color: '#d4d4d4', padding: 12, borderRadius: 6, fontSize: 12, margin: '4px 0', overflow: 'auto' }}>
                {s.example}
              </pre>
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 4 }}>
                <CopyBtn text={s.example} label="複製 curl" />
              </div>
            </>
          )}
          {s.response && (
            <>
              <Text type="secondary" style={{ fontSize: 12 }}>Response：</Text>
              <pre style={{ background: '#f0fff4', border: '1px solid #b7eb8f', padding: 10, borderRadius: 6, fontSize: 12, margin: '4px 0' }}>
                {s.response}
              </pre>
            </>
          )}
        </Card>
      ))}
    </div>
  );
}

// ── Main ───────────────────────────────────────────────────────────────────────
export default function ApiKeyPage() {
  const [keys, setKeys] = useState([]);
  const [createModal, setCreateModal] = useState(false);
  const [newKey, setNewKey] = useState(null);
  const [form] = Form.useForm();

  const reload = useCallback(async () => {
    const data = await api.listKeys();
    setKeys(Array.isArray(data) ? data : []);
  }, []);

  useEffect(() => { reload(); }, [reload]);

  const handleCreate = async () => {
    const vals = await form.validateFields();
    const res = await api.createKey({
      name: vals.name,
      permissions: vals.permissions || ['fb_ads:read', 'fb_ads:write'],
      notes: vals.notes,
      expires_days: vals.expires_days || null,
    });    if (res.api_key) {
      setNewKey(res);
      setCreateModal(false);
      form.resetFields();
      reload();
    } else {
      message.error(res.detail || '建立失敗');
    }
  };

  const cols = [
    {
      title: 'Key 名稱', dataIndex: 'name', render: (v, r) => (
        <Space direction="vertical" size={0}>
          <Text strong>{v}</Text>
          <Text type="secondary" style={{ fontSize: 12 }}>{r.key_prefix}••••••••</Text>
        </Space>
      ),
    },
    {
      title: '權限', dataIndex: 'permissions',
      render: perms => (perms || []).map(p => {
        const opt = PERM_OPTIONS.find(o => o.value === p);
        return <Tag key={p} color={opt?.color || 'default'} style={{ marginBottom: 2 }}>{opt?.label || p}</Tag>;
      }),
    },
    {
      title: '使用次數', dataIndex: 'request_count',
      render: v => <Statistic value={v || 0} style={{ '.ant-statistic-content': { fontSize: 14 } }} />,
      width: 100,
    },
    {
      title: '最後使用', dataIndex: 'last_used_at',
      render: v => v ? new Date(v).toLocaleString('zh-TW') : '從未使用',
      width: 140,
    },
    {
      title: '狀態', dataIndex: 'is_active', width: 80,
      render: (v, r) => (
        <Switch
          checked={v}
          size="small"
          onChange={async (checked) => {
            await api.toggleKey(r.id, { is_active: checked });
            reload();
          }}
        />
      ),
    },
    {
      title: '', width: 60,
      render: (_, r) => (
        <Popconfirm title="確定刪除此 API Key？" okText="刪除" cancelText="取消" okButtonProps={{ danger: true }}
          onConfirm={async () => { await api.deleteKey(r.id); reload(); }}>
          <Button danger type="text" size="small" icon={<DeleteOutlined />} />
        </Popconfirm>
      ),
    },
  ];

  return (
    <div style={{ padding: 24, maxWidth: 1100, margin: '0 auto' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <div>
          <Title level={4} style={{ margin: 0 }}>
            <ApiOutlined style={{ color: '#1890ff', marginRight: 8 }} />
            公開 API 管理
          </Title>
          <Text type="secondary">管理外部網站的 API 存取金鑰，供 EDM、活動報名平台、comboware.ai 等外部系統呼叫廣告投放與成效查詢功能</Text>
        </div>
        <Button type="primary" icon={<PlusOutlined />} onClick={() => setCreateModal(true)}>
          建立 API Key
        </Button>
      </div>

      <Tabs defaultActiveKey="keys">
        <TabPane tab={<span><KeyOutlined /> API Keys</span>} key="keys">
          <Table
            dataSource={keys}
            columns={cols}
            rowKey="id"
            pagination={false}
            size="middle"
            locale={{ emptyText: '尚無 API Key，點擊「建立 API Key」開始使用' }}
          />
        </TabPane>

        <TabPane tab={<span><BookOutlined /> API 文件</span>} key="docs">
          <ApiDocPanel />
        </TabPane>
      </Tabs>

      {/* Create modal */}
      <Modal title="建立 API Key" open={createModal} onOk={handleCreate}
        onCancel={() => { setCreateModal(false); form.resetFields(); }}
        okText="建立" cancelText="取消">
        <Form form={form} layout="vertical">
          <Form.Item name="name" label="Key 名稱" rules={[{ required: true, message: '請輸入名稱' }]}>
            <Input placeholder="例：活動報名系統、EDM 平台" />
          </Form.Item>
          <Form.Item name="permissions" label="權限" initialValue={['fb_ads:read', 'fb_ads:write', 'google_ads:read', 'google_ads:write']}>
            <Select mode="multiple" options={PERM_OPTIONS.map(o => ({ value: o.value, label: o.label }))} />
          </Form.Item>
          <Form.Item name="expires_days" label="有效天數（留空=永久）">
            <InputNumber min={1} max={3650} placeholder="例：365" style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="notes" label="備注">
            <Input.TextArea placeholder="說明此 Key 的用途..." rows={2} />
          </Form.Item>
        </Form>
      </Modal>

      {/* Show new key — only once */}
      {newKey && (
        <Modal
          title={<span><ExclamationCircleOutlined style={{ color: '#faad14', marginRight: 8 }} />請立即保存 API Key</span>}
          open={!!newKey}
          onOk={() => setNewKey(null)}
          onCancel={() => setNewKey(null)}
          okText="我已複製，關閉"
          cancelButtonProps={{ style: { display: 'none' } }}
          closable={false}
        >
          <Alert
            message="此 API Key 只顯示一次，關閉後無法再次查看！"
            type="warning" showIcon style={{ marginBottom: 16 }}
          />
          <div style={{ background: '#f6f8fa', padding: 16, borderRadius: 8, border: '1px solid #d9d9d9' }}>
            <Text strong style={{ fontSize: 15, fontFamily: 'monospace', wordBreak: 'break-all' }}>
              {newKey.api_key}
            </Text>
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 10 }}>
            <CopyBtn text={newKey.api_key} label="複製 API Key" />
          </div>
          <Divider />
          <Text type="secondary" style={{ fontSize: 12 }}>使用方式：</Text>
          <pre style={{ background: '#1e1e1e', color: '#d4d4d4', padding: 10, borderRadius: 6, fontSize: 12, marginTop: 6 }}>
{`curl -X POST https://spo.comboware.ai/api/v1/public/fb-ads \\
  -H "X-API-Key: ${newKey.api_key}" \\
  -H "Content-Type: application/json" \\
  -d '{"title":"廣告標題","body":"內文","cta_url":"https://your-site.com"}'`}
          </pre>
        </Modal>
      )}
    </div>
  );
}
